import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useContent } from "@/lib/content";
import { Sparkles } from "lucide-react";

const withBackend = (u) => (u && u.startsWith("/api/media/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

/**
 * Timeline38 — Vertical timeline with alternating events (left/right) around a
 * central line. Each event has an AI-generated pictogram icon (via CMS) and a
 * year + description. Rendered from `timeline_38` KV.
 */
export default function Timeline38({ hideTitle = false }) {
  const { timeline_38: t38 = {} } = useContent();
  const events = useMemo(() => (t38.events || []).slice().sort((a, b) => Number(a.year || 0) - Number(b.year || 0)), [t38.events]);
  if (!t38.enabled || events.length === 0) return null;

  return (
    <section className={`theme-light ${hideTitle ? "py-12 md:py-16" : "py-24 md:py-32 border-t border-black/10"} relative overflow-hidden`} data-testid="timeline-38">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {!hideTitle && (
          <div className="text-center mb-16 md:mb-20">
            <div className="text-xs uppercase tracking-widest text-accent-orange mb-4 flex items-center justify-center gap-2">
              <Sparkles className="w-3 h-3" /> {t38.eyebrow || "Linha do tempo"}
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl mx-auto">
              {t38.title || "38 anos construindo o futuro do marketing imobiliário."}
            </h2>
            {t38.subtitle && <p className="mt-6 text-base md:text-lg text-neutral-600 max-w-2xl mx-auto">{t38.subtitle}</p>}
          </div>
        )}

        <div className="relative max-w-5xl mx-auto">
          {/* Central vertical line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-accent-orange via-accent-orange/40 to-transparent md:-translate-x-[1px]" aria-hidden />

          <ol className="space-y-8 md:space-y-12">
            {events.map((ev, i) => {
              const isLeft = i % 2 === 0;
              const blockBg = ev.background_color || "";
              const blockCardStyle = blockBg ? { backgroundColor: blockBg } : undefined;
              return (
                <motion.li
                  key={`${ev.year}-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.05 * (i % 4) }}
                  className={`relative flex flex-col md:flex-row md:items-center ${isLeft ? "md:justify-start" : "md:justify-end"}`}
                  data-testid={`timeline-38-event-${i}`}
                >
                  {/* Dot on the line — radar ripple effect (same style as Brazil map) */}
                  <span className="absolute left-8 md:left-1/2 md:-translate-x-1/2 -translate-y-1/2 top-8 md:top-1/2 z-10 pointer-events-none" aria-hidden data-testid={`t38-radar-${i}`}>
                    <span className="relative flex items-center justify-center w-4 h-4">
                      {/* Outer ripple */}
                      <motion.span
                        className="absolute inset-0 rounded-full border-2 border-accent-orange"
                        initial={{ scale: 1, opacity: 0.6 }}
                        animate={{ scale: [1, 3.5], opacity: [0.6, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: (i * 0.15) % 1.2 }}
                      />
                      {/* Inner ripple (offset) */}
                      <motion.span
                        className="absolute inset-0 rounded-full border border-accent-orange"
                        initial={{ scale: 1, opacity: 0.4 }}
                        animate={{ scale: [1, 5], opacity: [0.4, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: ((i * 0.15) % 1.2) + 0.8 }}
                      />
                      {/* Solid dot */}
                      <span className="relative w-4 h-4 rounded-full bg-accent-orange ring-4 ring-white shadow-md shadow-accent-orange/40" />
                    </span>
                  </span>

                  {/* Content card — background color editable via CMS */}
                  <div className={`ml-14 md:ml-0 md:w-[calc(50%-2.5rem)] ${isLeft ? "md:pr-10 md:text-right" : "md:pl-10 md:text-left"}`}>
                    <div style={blockCardStyle} className={`${blockBg ? "rounded-lg p-4 md:p-5" : ""}`}>
                      <div className={`inline-flex items-center gap-3 mb-3 ${isLeft ? "md:flex-row-reverse" : ""}`}>
                        {ev.icon_url ? (
                          <img src={withBackend(ev.icon_url)} alt="" className="w-12 h-12 md:w-14 md:h-14 object-contain" style={{ filter: "drop-shadow(0 2px 8px rgba(255,94,0,0.15))" }} />
                        ) : (
                          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-accent-orange/10 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-accent-orange" />
                          </div>
                        )}
                        <div className={`font-display text-3xl md:text-4xl lg:text-5xl text-accent-orange leading-none`}>{ev.year}</div>
                      </div>
                      {ev.title && <div className="font-display text-lg md:text-xl mb-1.5 leading-tight">{ev.title}</div>}
                      <p className={`text-sm text-neutral-700 leading-snug ${isLeft ? "md:text-right" : "md:text-left"}`}>{ev.description}</p>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
